<template>
  <div class="ai-chat-container">
    <!-- 顶部操作栏 -->
    <div class="chat-header">
      <a-button type="primary" icon="plus" @click="createSession">新建对话</a-button>
      <span class="session-info" v-if="sessionId">当前会话：{{ sessionId }}</span>
      <span class="session-info" v-else style="color: #999;">请先新建或选择对话</span>
    </div>

    <!-- 聊天区域 -->
    <div class="chat-body" ref="chatBody">
      <div v-if="messages.length === 0" class="empty-chat">
        <a-icon type="message" style="font-size: 48px; color: #d9d9d9;" />
        <p style="margin-top: 16px; color: #999;">暂无对话记录，点击"新建对话"开始聊天</p>
      </div>
      <div
        v-for="(msg, index) in messages"
        :key="index"
        :class="['message-item', msg.role === 'user' ? 'message-user' : 'message-ai']"
      >
        <div class="message-avatar">
          <a-avatar :style="{ backgroundColor: msg.role === 'user' ? '#1890ff' : '#52c41a' }">
            <a-icon :type="msg.role === 'user' ? 'user' : 'robot'" />
          </a-avatar>
        </div>
        <div class="message-content">
          <div class="message-role">{{ msg.role === 'user' ? '我' : 'AI助手' }}</div>
          <div class="message-text" v-html="formatMessage(msg.content)"></div>
        </div>
      </div>
      <!-- AI正在回复 -->
      <div v-if="loading" class="message-item message-ai">
        <div class="message-avatar">
          <a-avatar style="background-color: #52c41a;">
            <a-icon type="robot" />
          </a-avatar>
        </div>
        <div class="message-content">
          <div class="message-role">AI助手</div>
          <div class="message-text typing">
            <a-spin size="small" />
            <span style="margin-left: 8px;">正在思考中...</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 输入区域 -->
    <div class="chat-footer">
      <a-textarea
        v-model="inputText"
        placeholder="请输入消息，按 Enter 发送，Shift+Enter 换行"
        :auto-size="{ minRows: 4, maxRows: 6 }"
        @keydown="handleKeydown"
        :disabled="!sessionId || loading"
      />
      <a-button
        type="primary"
        icon="send"
        :style="{ marginTop: '8px' }"
        :disabled="!inputText.trim() || !sessionId || loading"
        @click="sendMessage"
      >
        发送
      </a-button>
    </div>
  </div>
</template>

<script>
import { sendAiChat, createAiSession, getAiHistory } from "@/api/storeApi.js";
import { message } from "ant-design-vue";

export default {
  data() {
    return {
      sessionId: "",
      messages: [],
      inputText: "",
      loading: false
    };
  },
  methods: {
    createSession() {
      createAiSession('default').then(res => {
        if (res && res.data) {
          this.sessionId = res.data;
          this.messages = [];
          message.success("新对话已创建");
        }
      }).catch(() => {
        message.error("创建对话失败");
      });
    },
    sendMessage() {
      const text = this.inputText.trim();
      if (!text) return;
      this.messages.push({ role: "user", content: text });
      this.inputText = "";
      this.loading = true;
      this.scrollToBottom();

      sendAiChat({
        sessionId: this.sessionId,
        message: text,
        scenario:"default"
      }).then(res => {
        this.loading = false;
        if (res && res.data) {
          if (res.data.sessionId) {
            this.sessionId = res.data.sessionId;
          }
          if (res.data.reply) {
            this.messages.push({ role: "assistant", content: res.data.reply });
          } else if (res.data.errorMsg) {
            message.error(res.data.errorMsg);
          }
        }
        this.scrollToBottom();
      }).catch(() => {
        this.loading = false;
        message.error("发送失败，请重试");
      });
    },
    handleKeydown(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    formatMessage(content) {
      if (!content) return "";
      return content.replace(/\n/g, "<br/>");
    },
    scrollToBottom() {
      this.$nextTick(() => {
        var body = this.$refs.chatBody;
        if (body) {
          body.scrollTop = body.scrollHeight;
        }
      });
    }
  }
};
</script>

<style scoped>
.ai-chat-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
}

.chat-header {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
}

.session-info {
  margin-left: 16px;
  font-size: 13px;
  color: #666;
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.message-item {
  display: flex;
  margin-bottom: 20px;
}

.message-user {
  flex-direction: row-reverse;
}

.message-user .message-content {
  align-items: flex-end;
}

.message-ai .message-content {
  align-items: flex-start;
}

.message-avatar {
  flex-shrink: 0;
  margin: 0 12px;
}

.message-content {
  display: flex;
  flex-direction: column;
  max-width: 70%;
}

.message-role {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.message-text {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
}

.message-user .message-text {
  background: #1890ff;
  color: #fff;
  border-top-right-radius: 2px;
}

.message-ai .message-text {
  background: #f0f0f0;
  color: #333;
  border-top-left-radius: 2px;
}

.typing {
  display: flex;
  align-items: center;
}

.chat-footer {
  padding: 12px 24px;
  border-top: 1px solid #e8e8e8;
  background: #fafafa;
}
</style>



// WEBPACK FOOTER //
// src/views/systemManagement/aiChat/aiChat.vue